import { Component, ErrorInfo, ReactNode } from 'react';

import { Link } from 'react-router-dom';

import { Card } from '@/components/Card';
import { Container } from '@/components/layout/Container';
import { HomeRoute } from '@/configs/routes';

interface Props {
  component: string;
  children?: ReactNode;
  locationKey: string;
}

interface State {
  hasError: boolean;
  error?: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary - Component:', this.props.component, ' Uncaught error:', error, errorInfo);
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.locationKey !== this.props.locationKey && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  private getErrorStatus(error?: Error | null): number | undefined {
    if (!error) return undefined;
    const withStatus = error as Error & { status?: number; statusCode?: number };
    return withStatus.status ?? withStatus.statusCode;
  }

  public render() {
    if (this.state.hasError) {
      const status = this.getErrorStatus(this.state.error);
      const isForbidden = status === 403;

      return (
        <Container>
          <Card className="w-full flex flex-col items-center">
            <p className="font-display text-4xl font-semibold text-[#274483] sm:text-5xl">
              {isForbidden ? 'Permission denied' : 'Something went wrong :('}
            </p>
            <p className="mt-2 text-sm text-neutral-600">
              {isForbidden
                ? 'You do not have permission to access this data. The API key may be invalid. Contact the service team to refresh credentials.'
                : 'Check the browser console logs for the stack trace.'}
            </p>
            <Link
              to={HomeRoute}
              className="mt-4 text-sm font-semibold text-neutral-950 transition hover:text-neutral-700"
            >
              Go to the home page
            </Link>
          </Card>
        </Container>
      );
    }

    return this.props.children;
  }
}
